import dicomParser from "dicom-parser";
import axiosInterceptor from "../../../helpers/Interceptor";
import * as cornerstone from "@cornerstonejs/core";
import { generateDicomUID } from "./DicomUidUtility";

/**
 * Standard DICOM Key Object Selection Document Titles (CID 7010).
 * Each entry: { code, meaning } — code is the DCM code value, meaning is the
 * human-readable label shown in the Mark as KO dialog dropdown.
 */
export const KO_DOCUMENT_TITLES = [
  { code: "113000", meaning: "Of Interest" },
  { code: "113001", meaning: "Rejected for Quality Reasons" },
  { code: "113002", meaning: "For Referring Provider" },
  { code: "113003", meaning: "For Surgery" },
  { code: "113004", meaning: "For Teaching" },
  { code: "113005", meaning: "For Conference" },
  { code: "113006", meaning: "For Therapy" },
  { code: "113007", meaning: "For Patient" },
  { code: "113008", meaning: "For Peer Review" },
  { code: "113009", meaning: "For Research" },
  { code: "113010", meaning: "Quality Issue" },
  { code: "113013", meaning: "Best In Set" },
];

// ─── KO parse cache ───────────────────────────────────────────────────────────
// One entry per KO DICOM file (keyed by URL).
const koCache = new Map();

/** Download and parse a KO DICOM file from the given URL.  Cached by URL. */
export const parseKOFile = async (url) => {
  if (koCache.has(url)) return koCache.get(url);

  const res         = await axiosInterceptor.get(url, { responseType: "blob" });
  const arrayBuffer = await res.data.arrayBuffer();
  const byteArray   = new Uint8Array(arrayBuffer);
  const dataSet     = dicomParser.parseDicom(byteArray);
  const koData      = extractKOData(dataSet);
  koCache.set(url, koData);
  return koData;
};

/**
 * Extract a KO Document's metadata + the list of source SOP Instance UIDs it
 * references.  Handles both Content Sequence (0040,A730) IMAGE items and
 * Current Requested Procedure Evidence Sequence (0040,A375) references so we
 * pick up images regardless of which form the KO writer used.
 */
const extractKOData = (dataSet) => {
  const ko = {
    referencedSOPInstanceUIDs: [],
    title:        "",
    description:  "",
    creatorName:  "",
    creationDate: "",
  };

  // Concept Name Code Sequence (0040,A043) → Code Meaning (0008,0104) is the
  // KO Document Title (e.g. "Of Interest", "For Teaching").
  const conceptNameSeq = dataSet.elements["x0040a043"];
  if (conceptNameSeq?.items?.length) {
    ko.title = conceptNameSeq.items[0].dataSet.string("x00080104")?.trim() ?? "";
  }

  // Content Description (0070,0081), Creator (0070,0084), Creation Date.
  ko.description  = dataSet.string("x00700081")?.trim() ?? "";
  ko.creatorName  = dataSet.string("x00700084")?.trim() ?? "";
  ko.creationDate = dataSet.string("x00700082")?.trim()
                 ?? dataSet.string("x00080023")?.trim() ?? "";

  // Content Sequence (0040,A730) — IMAGE items reference the source SOPs,
  // TEXT items can carry a free-text description if (0070,0081) is empty.
  const contentSeq = dataSet.elements["x0040a730"];
  if (contentSeq?.items) {
    contentSeq.items.forEach((item) => {
      const valueType = item.dataSet.string("x0040a040")?.trim();
      if (valueType === "IMAGE") {
        const refSopSeq = item.dataSet.elements["x00081199"];
        if (refSopSeq?.items) {
          refSopSeq.items.forEach((refItem) => {
            const uid = refItem.dataSet.string("x00081155")?.trim();
            if (uid && !ko.referencedSOPInstanceUIDs.includes(uid)) {
              ko.referencedSOPInstanceUIDs.push(uid);
            }
          });
        }
      } else if (valueType === "TEXT" && !ko.description) {
        ko.description = item.dataSet.string("x0040a160")?.trim() ?? "";
      }
    });
  }

  // Current Requested Procedure Evidence Sequence (0040,A375) is the standard
  // way to point at the source series/images — fall back to it for KO writers
  // that don't use the IMAGE Content Sequence approach.
  const evidenceSeq = dataSet.elements["x0040a375"];
  if (evidenceSeq?.items) {
    evidenceSeq.items.forEach((evItem) => {
      const refSeriesSeq = evItem.dataSet.elements["x00081115"];
      if (refSeriesSeq?.items) {
        refSeriesSeq.items.forEach((seriesItem) => {
          const refSopSeq = seriesItem.dataSet.elements["x00081199"];
          if (refSopSeq?.items) {
            refSopSeq.items.forEach((refItem) => {
              const uid = refItem.dataSet.string("x00081155")?.trim();
              if (uid && !ko.referencedSOPInstanceUIDs.includes(uid)) {
                ko.referencedSOPInstanceUIDs.push(uid);
              }
            });
          }
        });
      }
    });
  }

  return ko;
};

/** Returns all parsed KO data objects (one per cached file). */
export const getAllCachedKOData = () => [...koCache.values()];

/** Returns { url, koData } pairs for every cached KO. */
export const getAllCachedKOEntries = () =>
  [...koCache.entries()].map(([url, data]) => ({ url, data }));

/** Clear the KO parse cache (call on study unload if needed). */
export const clearKOCache = () => koCache.clear();

// ─── Save payload helpers (Mark as KO dialog) ────────────────────────────────

/**
 * Collect the metadata required to save the currently displayed image as a
 * Key Object Selection Document.
 */
export const collectKOSavePayload = (
  viewport,
  imageId,
  studyId,
  { titleCode = "113000", titleMeaning = "Of Interest", description = "", creatorName = "" } = {}
) => {
  const sopMeta    = cornerstone.metaData.get("sopCommonModule",     imageId);
  const seriesMeta = cornerstone.metaData.get("generalSeriesModule", imageId);

  const sopInstanceUID    = (sopMeta?.sopInstanceUID    ?? sopMeta?.SOPInstanceUID    ?? "").trim();
  const seriesInstanceUID = (seriesMeta?.seriesInstanceUID ?? seriesMeta?.SeriesInstanceUID ?? "").trim();

  return {
    studyId,
    seriesInstanceUID,
    sopInstanceUID,
    // Client-generated UID for the NEW KO series about to be created. The backend
    // uses this verbatim (falling back to its own UID only if omitted), so the
    // caller can look up this exact series once async DICOM ingestion completes,
    // instead of re-fetching the whole study (see waitForSeriesReady / getSeriesByUID).
    newSeriesInstanceUID: generateDicomUID(),
    titleCode,
    titleMeaning,
    description: description.trim() || undefined,
    creatorName: creatorName.trim() || undefined,
  };
};

/** POST the KO save payload to the backend. */
export const saveKOAnnotations = (payload) =>
  axiosInterceptor.post("/jwtServices/study/save-ko", payload);
